import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { I18nProvider } from 'lingui-react';
import { unpackCatalog } from 'lingui-i18n';
import { snakeCase } from 'lodash';

import { getLanguageSelector } from 'redux/preferences/selectors';

import Loading from 'components/Loading';

// required in development only (huge dependency)
// eslint-disable-next-line global-require
const dev = process.env.NODE_ENV !== 'production' && require('lingui-i18n/dev');

// FIXME: cannot test this until we figure out how to mock the async module loading
// If we figure out how to test this, remove it from the coverage exclusions in package.json
export class I18nLoader extends React.Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    language: PropTypes.string.isRequired,
  };

  state = {
    catalogs: {},
  };

  componentDidMount() {
    // `shouldComponentUpdate` isn’t called during the first render, so we need trigger
    // loading of catalog manually in `componentDidMount`:
    this.loadCatalog(this.props.language);
  }

  shouldComponentUpdate(nextProps, nextState) {
    // We need to load message catalog when language changes. Dynamic import returns
    // a promise and we don’t want to re-render our component until the message catalog
    // is loaded.
    const { language } = nextProps;
    const { catalogs } = nextState;

    if (language !== this.props.language && !catalogs[language]) {
      // Start loading message catalog and skip update
      this.loadCatalog(language);
      return false;
    }

    return true;
  }

  /**
   * Dynamically loads the message catalog for a given language.
   * @param {string} language - e.g. 'en' or 'es'
   */
  loadCatalog = async language => {
    const locale = snakeCase(language);
    let catalog;
    try {
      catalog = await import(/* webpackMode: "lazy", webpackChunkName: "i18n-[index]" */
      `locale/${locale}/messages.js`);
    } catch (error) {
      // TODO: Handle the error when there is no locale available
      // console.log(error);

      // Falls back to en-US
      catalog = await import(/* webpackMode: "lazy", webpackChunkName: "i18n-[index]" */
      'locale/en_us/messages.js');
    }

    this.setState(state => ({
      catalogs: {
        ...state.catalogs,
        [language]: unpackCatalog(catalog),
      },
    }));
  };

  render() {
    const { children, language } = this.props;
    const { catalogs } = this.state;

    // Skip rendering when catalog isn't loaded.
    if (!catalogs[language]) {
      // FIXME: we need a better "loading" experience
      return <Loading />;
    }

    return (
      <I18nProvider language={language} development={dev} catalogs={catalogs}>
        {children}
      </I18nProvider>
    );
  }
}

function mapStateToProps(state) {
  return {
    language: getLanguageSelector(state),
  };
}

const ConnectedI18nLoader = connect(mapStateToProps)(I18nLoader);

function TestableI18nLoader(props) {
  if (process.env.NODE_ENV === 'test') {
    const catalogs = {
      // eslint-disable-next-line global-require
      en: unpackCatalog(require('../locale/en_us/messages.js')),
    };

    return (
      <I18nProvider language="en" development={false} catalogs={catalogs}>
        {props.children}
      </I18nProvider>
    );
  }

  return <ConnectedI18nLoader {...props} />;
}

TestableI18nLoader.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TestableI18nLoader;



// WEBPACK FOOTER //
// ./src/setup/I18nLoader.js